import { computed } from 'vue'
import { CONFIG } from '@/config'

/**
 * Composable for accessing chat configuration
 *
 * Provides access to validation limits like max characters
 * and allowed languages for chat prompts.
 * Configuration is loaded from front-config endpoint.
 */
export function useChatConfig() {
  // Default fallback values
  const DEFAULT_CONFIG = {
    max_user_prompt_chars: 4000,
    allowed_languages: ['en'],
    english_ascii_threshold: 0.8
  }

  /**
   * Get chat configuration from CONFIG
   */
  const getChatConfig = () => {
    return CONFIG?.chat || DEFAULT_CONFIG
  }

  /**
   * Validate a message against the configuration
   */
  const validateMessage = (text) => {
    const chatConfig = getChatConfig()
    const trimmedText = text.trim()
    const charCount = trimmedText.length

    if (charCount === 0) {
      return {
        isValid: false,
        error: 'Message cannot be empty',
        charCount: 0
      }
    }

    if (charCount > chatConfig.max_user_prompt_chars) {
      return {
        isValid: false,
        error: `Message exceeds maximum length of ${chatConfig.max_user_prompt_chars} characters`,
        charCount
      }
    }

    // Simple English-only check (ASCII threshold from config)
    // Only enforce if English is the sole allowed language
    if (
      chatConfig.allowed_languages.includes('en') &&
      chatConfig.allowed_languages.length === 1
    ) {
      // Count ASCII characters efficiently
      let asciiChars = 0
      for (let i = 0; i < trimmedText.length; i++) {
        if (trimmedText.charCodeAt(i) < 128) asciiChars++
      }

      const asciiRatio = asciiChars / charCount
      const threshold = chatConfig.english_ascii_threshold

      if (asciiRatio < threshold) {
        return {
          isValid: false,
          error: 'Only English messages are supported',
          charCount
        }
      }
    }

    return {
      isValid: true,
      error: null,
      charCount
    }
  }

  // Computed properties for easy access
  const maxChars = computed(() => {
    const chatConfig = getChatConfig()
    return chatConfig.max_user_prompt_chars
  })

  const allowedLanguages = computed(() => {
    const chatConfig = getChatConfig()
    return chatConfig.allowed_languages
  })

  return {
    // Computed
    maxChars,
    allowedLanguages,

    // Methods
    validateMessage
  }
}
